import { Component, EventEmitter, Input, Output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { IonIcon } from '@ionic/angular/standalone';
import { OtpInputComponent } from '@appchezandre/angular-ui';
import { CertificateService } from '../../services/http/certificate.service';
import { StudentVerificationService } from '../../services/http/student-verification.service';
import { AuthService } from '../../services/http/auth.service';
import { StorageService } from '../../services/storage.service';

type Mode      = 'choice' | 'certificate' | 'email';
type EmailStep = 'input' | 'otp';

@Component({
  selector: 'app-certificat-rejete',
  standalone: true,
  imports: [IonIcon, FormsModule, OtpInputComponent],
  templateUrl: './certificat-rejete.component.html',
  styleUrls: ['./certificat-rejete.component.scss'],
})
export class CertificatRejeteComponent {
  @Input() rejectionReason: string | null = null;
  @Output() refresh = new EventEmitter<void>();
  @Output() logout  = new EventEmitter<void>();

  // ─── Navigation ────────────────────────────────────────────────────────────
  readonly mode      = signal<Mode>('choice');
  readonly emailStep = signal<EmailStep>('input');

  // ─── Certificate upload ────────────────────────────────────────────────────
  selectedFile  = signal<File | null>(null);
  isDragOver    = signal(false);
  isUploading   = signal(false);
  uploadSuccess = signal(false);
  uploadError   = signal('');

  // ─── Email / OTP ───────────────────────────────────────────────────────────
  studentEmail     = signal('');
  otpCode          = signal('');
  isSendingOtp     = signal(false);
  isVerifyingOtp   = signal(false);
  emailError       = signal('');

  constructor(
    private readonly certificateService: CertificateService,
    private readonly studentVerificationService: StudentVerificationService,
    private readonly authService: AuthService,
    private readonly storageService: StorageService,
  ) {}

  // ─── Mode navigation ───────────────────────────────────────────────────────

  goTo(m: Mode) {
    this.mode.set(m);
    this.uploadError.set('');
    this.emailError.set('');
  }

  // ─── Certificate upload ────────────────────────────────────────────────────

  onDragOver(e: DragEvent) {
    e.preventDefault();
    this.isDragOver.set(true);
  }

  onDragLeave() {
    this.isDragOver.set(false);
  }

  onDrop(e: DragEvent) {
    e.preventDefault();
    this.isDragOver.set(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) this.selectFile(file);
  }

  onFileChange(e: Event) {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    if (file) this.selectFile(file);
    input.value = '';
  }

  private selectFile(file: File) {
    const error = this.certificateService.getFileValidationError(file);
    if (error) { this.uploadError.set(error); return; }
    this.selectedFile.set(file);
    this.uploadError.set('');
  }

  clearFile() {
    this.selectedFile.set(null);
    this.uploadError.set('');
  }

  async submitCertificate() {
    const file = this.selectedFile();
    if (!file) return;

    const user = this.storageService.getUser();
    this.isUploading.set(true);
    this.uploadError.set('');

    try {
      await this.certificateService.uploadCertificate(
        user?.id ?? '',
        file,
        user?.email,
        user?.prenom,
        user?.nom,
      );
      this.uploadSuccess.set(true);
      // Refresh profile after ~1s so parent can transition to ATTENTE screen
      setTimeout(() => this.refresh.emit(), 1200);
    } catch (err: any) {
      this.uploadError.set(
        err?.error?.message ?? 'Erreur lors de l\'envoi. Veuillez réessayer.'
      );
    } finally {
      this.isUploading.set(false);
    }
  }

  // ─── Email OTP ─────────────────────────────────────────────────────────────

  async sendOtp() {
    const email = this.studentEmail().trim().toLowerCase();
    if (!email || !email.includes('@') || !email.includes('.')) {
      this.emailError.set('Email invalide');
      return;
    }
    this.isSendingOtp.set(true);
    this.emailError.set('');
    try {
      await this.studentVerificationService.sendOtp(email);
      this.emailStep.set('otp');
    } catch (err: any) {
      this.emailError.set(err?.error?.message ?? 'Erreur lors de l\'envoi du code.');
    } finally {
      this.isSendingOtp.set(false);
    }
  }

  onOtpChange(otp: string)  { this.otpCode.set(otp); }
  onOtpComplete()           { this.verifyOtp(); }
  backToEmailInput()        { this.emailStep.set('input'); this.otpCode.set(''); this.emailError.set(''); }

  async verifyOtp() {
    const email = this.studentEmail().trim().toLowerCase();
    const code  = this.otpCode().trim();
    if (!code || code.length !== 6) { this.emailError.set('Code à 6 chiffres requis'); return; }

    this.isVerifyingOtp.set(true);
    this.emailError.set('');
    try {
      const verify = await this.studentVerificationService.verifyOtp(email, code);
      if (!verify.valid) { this.emailError.set('Code invalide ou expiré'); return; }

      const response = await this.authService.updateStudentEmail({ email, code });
      if (response.access_token) {
        this.storageService.setToken(
          response.access_token,
          this.storageService.isRememberMeActive(),
          this.storageService.getRefreshToken() ?? undefined,
        );
        const profile = await this.authService.getProfile();
        this.storageService.setUser(profile);
      }
      this.refresh.emit();
    } catch (err: any) {
      this.emailError.set(err?.error?.message ?? 'Erreur de vérification. Veuillez réessayer.');
    } finally {
      this.isVerifyingOtp.set(false);
    }
  }
}
